import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Domain as DomainIcon,
  Apartment as ApartmentIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import useDelete from '../../api/useDelete';

const UserView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token;

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/users/${id}`;
  const headers = token ? { Authorization: `Token ${token}` } : {};

  const { 
    data: userData, 
    isLoading: userLoading, 
    isError: userError,
    error: userErrorDetails,
  } = useQuery({
    queryKey: ['users', id],
    queryFn: async () => {
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    }
  });

  const { handleDelete, ConfirmDialog } = useDelete('users', () => {
    toast.success(t('users.delete_success', 'User deleted successfully'));
    navigate('/users');
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    return status ? 'success' : 'error';
  };

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userError) {
    return (
      <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
        {userErrorDetails?.message || t('users.fetch_error', 'Failed to load user data')}
      </Alert>
    );
  }

  if (!userData) {
    return (
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        {t('users.not_found', 'User not found')}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header with Back Button and Actions */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={t('common.back', 'Back')}>
            <IconButton
              component={RouterLink}
              to="/users"
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2
            }}
          >
            {userData.username}
            <Typography 
              component="span" 
              variant="body1" 
              sx={{ 
                display: 'block',
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              {userData.position || t('users.no_position', 'No position specified')}
            </Typography>
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleDelete(id)}
            sx={{ 
              textTransform: 'none',
              minWidth: 100
            }}
          >
            {t('common.delete', 'Delete')}
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            component={RouterLink}
            to={`/users/${id}/edit`}
            sx={{ 
              textTransform: 'none',
              minWidth: 100
            }}
          >
            {t('common.edit', 'Edit')}
          </Button>
        </Box>
      </Box>

      {/* User Profile Content */}
      <Grid container spacing={3}>
        {/* User Avatar and Basic Info */}
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Avatar 
                sx={{ 
                  width: 120, 
                  height: 120,
                  bgcolor: 'primary.main',
                  mb: 2,
                  fontSize: '3rem'
                }}
              >
                {userData.username.charAt(0).toUpperCase()}
              </Avatar>
              <Chip 
                label={userData.is_active ? 
                  t('users.status_active', 'Active') : 
                  t('users.status_inactive', 'Inactive')}
                color={getStatusColor(userData.is_active)}
                size="medium"
                sx={{ 
                  fontWeight: 600,
                  px: 2,
                  py: 1
                }}
              />
            </Box>
            
            <CardContent>
              <List sx={{ width: '100%' }}>
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EmailIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {t('users.email', 'Email')}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {userData.email || '-'}
                      </Typography>
                    }
                  />
                </ListItem>
                
                <Divider component="li" sx={{ my: 1 }} />
                
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <WorkIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {t('users.role', 'Role')}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2">
                        {userData.role?.name || '-'}
                      </Typography>
                    }
                  />
                </ListItem>
                
                <Divider component="li" sx={{ my: 1 }} />

                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ApartmentIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {t('users.deputy_ministry', 'Deputy Ministry')}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2">
                        {userData.deputyMinistry?.name || '-'}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Detailed User Information */}
        <Grid item xs={12} md={8}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%'
            }}
          >
            <CardHeader 
              title={
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('users.details', 'User Details')}
                </Typography>
              }
              sx={{ 
                borderBottom: '1px solid',
                borderColor: 'divider',
                py: 2
              }}
            />
            
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <DomainIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {t('users.directorate', 'Directorate')}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2">
                            {userData.directorate?.name || '-'}
                          </Typography>
                        }
                      />
                    </ListItem>
                    
                    <Divider component="li" sx={{ my: 1 }} />
                    
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <BusinessIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {t('users.department', 'Department')}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2">
                            {userData.department?.name || '-'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <List>
                    {userData.is_provincial && (
                      <>
                        <ListItem sx={{ px: 0, py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <LocationIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                {t('users.province', 'Province')}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2">
                                {userData.province?.name || '-'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <Divider component="li" sx={{ my: 1 }} />
                      </>
                    )}
                    
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <AccessTimeIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {t('users.last_login', 'Last Login')}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2">
                            {formatDate(userData.last_login)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    
                    <Divider component="li" sx={{ my: 1 }} />
                    
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <BadgeIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {t('users.position', 'Position')}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2">
                            {userData.position || '-'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <ConfirmDialog />
    </Box>
  );
};

export default UserView;